import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { ColorModeSwitcher } from "../../ColorModeSwitcher";
import { ButtonPrimary } from "../../components/button/ButtonPrimary";
import { PasswordInput } from "../../components/input/PasswordInput";
import useCustomToast from "../../lib/useCustomToast";

const initialValues = {
  password: undefined,
  confirmationPassword: undefined,
};

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password harus diisi")
    .min(8, "Password harus memiliki minimal 8 karakter")
    .max(32, "Password tidak boleh lebih dari 32 karakter")
    .matches(/[a-z]/, "Password harus mengandung huruf kecil")
    .matches(/[A-Z]/, "Password harus mengandung huruf besar")
    .matches(/[0-9]/, "Password harus mengandung angka"),
  confirmationPassword: yup
    .string()
    .required("Konfirmasi password harus diisi")
    .min(8, "Password harus memiliki minimal 8 karakter")
    .max(32, "Password tidak boleh lebih dari 32 karakter")
    .matches(/[a-z]/, "Password harus mengandung huruf kecil")
    .matches(/[A-Z]/, "Password harus mengandung huruf besar")
    .matches(/[0-9]/, "Password harus mengandung angka")
    .oneOf([yup.ref("password")], "Password harus sama"),
});

export const ResetPassword = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useCustomToast();
  const nativeToast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const email = queryParams.get("email");

  useEffect(() => {
    if (!token && !email) {
      navigate("/", { replace: true });
      return toast({
        title: "Tidak memiliki akses!",
      });
    }
  }, [token, email, toast, navigate]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);

      const request = {
        token: token,
        email: email,
        password: values.password,
        password_confirmation: values.confirmationPassword,
      };

      axios
        .post(
          `${process.env.REACT_APP_API_URL}/v1/auth/buyer/reset-password`,
          request
        )
        .then((res) => {
          const redirect = new Promise((resolve, reject) => {
            setTimeout(() => {
              resolve(5000);
              navigate("/login");
            }, 5500);
          });

          nativeToast.promise(redirect, {
            success: {
              title: "Mengarahkan sekarang",
              description:
                "Anda bisa login menggunakan password anda yang baru",
            },
            error: {
              title: "Gagal mengarahkan",
              description: "Refresh halaman untuk tetap terhubung",
            },
            loading: {
              title: res.data.message,
              description:
                "Tunggu beberapa saat lagi, Kami akan mengarahkan anda ke halaman login...",
            },
          });
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });
  return (
    <VStack className="reset-password" w={"100%"} my={"auto"}>
      <HStack className="wrap-logo">
        <Image
          src="https://placehold.co/600x400/000000/FFFFFF/png"
          w={"100%"}
          maxW={"28px"}
        />
        <Text textStyle={"h-title"}>BisnisLink</Text>
      </HStack>

      <Text textStyle={"h-title"} mt={6}>
        Reset Ulang Password Akun Anda
      </Text>
      <Text variant={"secondary"} textStyle={"h-subtitle"} textAlign={"center"}>
        Atur password baru untuk segala akun anda dan amankan akses ke akun anda
      </Text>

      <form
        id="resetPasswordForm"
        onSubmit={formik.handleSubmit}
        style={{ width: "100%", marginTop: "40px" }}
      >
        <VStack w={"100%"} gap={6}>
          <FormControl
            isInvalid={
              formik.errors.password && formik.touched.password ? true : false
            }
          >
            <FormLabel htmlFor="password">Password</FormLabel>
            <PasswordInput
              onConfirm={(input) => {
                formik.setFieldValue("password", input);
              }}
            />
            <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={
              formik.errors.confirmationPassword &&
              formik.touched.confirmationPassword
                ? true
                : false
            }
          >
            <FormLabel htmlFor="confirmationPassword">
              Konfirmasi Password
            </FormLabel>
            <PasswordInput
              onConfirm={(input) => {
                formik.setFieldValue("confirmationPassword", input);
              }}
            />
            <FormErrorMessage>
              {formik.errors.confirmationPassword}
            </FormErrorMessage>
          </FormControl>

          <ButtonPrimary
            form="resetPasswordForm"
            isLoading={loading}
            loadingText="Loading"
            spinnerPlacement="start"
            type="submit"
            w={"100%"}
            colorScheme="red"
          >
            Reset Password
          </ButtonPrimary>

          <ColorModeSwitcher
            position={"absolute"}
            bottom={0}
            right={0}
            fontSize={"24px"}
            mb={4}
            mr={4}
          />
        </VStack>
      </form>
    </VStack>
  );
};
